import React, {
  useState,
  useCallback,
  ChangeEvent,
  KeyboardEvent,
  FocusEvent,
  Ref,
  forwardRef,
  useRef,
  useEffect,
} from "react";

import classnames from "classnames";

import { StyledAutoResizeTextarea } from "./Styles";
import useForkRef from "../../utils/useForkRef";
import { InputMode } from "../Input/types";

export interface IAutoResizeTextarea
  extends React.ComponentPropsWithoutRef<"textarea"> {
  minRows?: number;
  maxRows?: number;
  lineHeight?: number;
  preventNewRowOnEnter?: boolean;
  cursorAtTextEnd?: boolean;
  value?: string;
  mode?: InputMode;
}

export const AutoResizeTextarea = forwardRef(
  (
    {
      minRows = 1,
      maxRows = 6,
      lineHeight = 18,
      onChange,
      className,
      preventNewRowOnEnter = false,
      cursorAtTextEnd = false,
      onKeyDown,
      value,
      mode = "outlined",
      ...rest
    }: IAutoResizeTextarea,
    ref: Ref<HTMLTextAreaElement> | null | undefined
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const [rows, setRows] = useState(minRows);

    const handleRef = useForkRef(ref, innerRef);

    const calcRows = useCallback(
      (elem: HTMLTextAreaElement | null) => {
        if (elem) {
          const previousRows = elem.rows || minRows;

          elem.rows = minRows;
          const currentRows = Math.floor(elem.scrollHeight / lineHeight);

          if (currentRows === previousRows) {
            elem.rows = currentRows;
          }

          if (currentRows >= maxRows) {
            elem.rows = maxRows;
            elem.scrollTop = elem.scrollHeight;
          }

          setRows(currentRows < maxRows ? currentRows : maxRows);
        }
      },
      [lineHeight, maxRows, minRows]
    );

    useEffect(() => {
      calcRows(innerRef.current);
    }, [value, calcRows]);

    const handleOnChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        calcRows(e.target);
        typeof onChange === "function" && onChange(e);
      },
      [onChange, calcRows]
    );

    const handleFocus = useCallback(
      (e: FocusEvent<HTMLTextAreaElement>) => {
        if (cursorAtTextEnd) {
          e.target.setSelectionRange(
            e.target.value.length,
            e.target.value.length
          );
        }
      },
      [cursorAtTextEnd]
    );

    const handleTextAreaKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (preventNewRowOnEnter && e.key === "Enter") {
          e.preventDefault();
        }
        typeof onKeyDown === "function" && onKeyDown(e);
      },
      [preventNewRowOnEnter, onKeyDown]
    );

    return (
      <StyledAutoResizeTextarea
        {...rest}
        value={value}
        ref={handleRef}
        onChange={handleOnChange}
        onKeyDown={handleTextAreaKeyDown}
        onFocus={handleFocus}
        rows={rows}
        mode={mode}
        className={classnames("c-autoresizetextarea", className)}
        style={{
          minHeight: `${lineHeight}px`,
          lineHeight: `${lineHeight}px`,
        }}
      />
    );
  }
);

AutoResizeTextarea.displayName = "AutoResizeTextarea";
